import {
  ReactElement,
  cloneElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Outlet, useLocation } from "react-router-dom";

import MobileSidebarWrapper from "./MobileSidebarWrapper";
import { useProfileCompletion } from "../../hooks/useProfileCompletion";
import ProfileCompletionModal from "../student/onboarding/ProfileCompletionModal";

interface PortalLayoutProps {
  sidebar: ReactElement;
}

// ─── Theme control ───────────────────────────────────────────────────────────
// Flip these constants to set the theme for each portal side independently.
// "dark" | "light" | "system"
const STUDENT_THEME: "dark" | "light" | "system" = "dark";
const COUNSELOR_THEME: "dark" | "light" | "system" = "dark"; // change to "light" to restore light theme
// ─────────────────────────────────────────────────────────────────────────────

function PortalLayout({ sidebar }: PortalLayoutProps) {
  const location = useLocation();
  const isStudentRoute = location.pathname.startsWith("/student");
  const {
    needsCompletion,
    loading: profileLoading,
    completeProfile,
  } = useProfileCompletion();

  // Apply theme based on which portal side is active.
  // Student routes use STUDENT_THEME; counselor routes use COUNSELOR_THEME.
  // Swap either constant at the top of this file to change — no other code needs to change.
  useEffect(() => {
    const el = document.documentElement;
    const theme = isStudentRoute ? STUDENT_THEME : COUNSELOR_THEME;

    if (theme === "dark") {
      el.classList.add("dark");
    } else if (theme === "light") {
      el.classList.remove("dark");
    } else {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      if (prefersDark) el.classList.add("dark");
      else el.classList.remove("dark");
    }
  }, [isStudentRoute]);

  const isCollegesRoute = useMemo(
    () =>
      location.pathname.startsWith("/student/colleges") ||
      location.pathname.startsWith("/counselor/colleges"),
    [location.pathname],
  );

  const [isMiniSidebar, setIsMiniSidebar] = useState(isCollegesRoute);

  useEffect(() => {
    setIsMiniSidebar(isCollegesRoute);
  }, [isCollegesRoute]);

  const effectivelyCollapsed = isMiniSidebar;

  const clonedSidebar = useMemo(
    () =>
      cloneElement(sidebar, {
        collapsed: effectivelyCollapsed,
        onToggleCollapse: () => {
          setIsMiniSidebar((prev) => !prev);
        },
      }),
    [sidebar, effectivelyCollapsed],
  );

  return (
    <div
      className={`relative h-[100dvh] overflow-hidden ${isStudentRoute ? "bg-white dark:bg-black text-black dark:text-white" : "bg-white dark:bg-[#03010a] text-black dark:text-white"}`}
    >
      {/* Profile Completion Modal - Only for student routes */}
      {isStudentRoute && !profileLoading && needsCompletion && (
        <ProfileCompletionModal onComplete={completeProfile} />
      )}

      {/* Background layers.
          Student routes: solid black in dark mode, white in light mode — clean, no gradient noise.
          Counselor routes: white in light mode, dark gradient in dark mode. */}
      <div
        className={`fixed inset-0 -z-30 ${isStudentRoute ? "bg-white dark:bg-black" : "bg-white dark:bg-[#03010a]"}`}
      />

      {/* Subtle purple gradient overlays — counselor dark mode only.
          Hidden on student routes; restore by adding isStudentRoute condition if desired. */}
      <div
        className={`fixed inset-0 -z-20 pointer-events-none hidden${isStudentRoute ? "" : " dark:block"}`}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,rgba(111,66,255,0.25),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(147,108,255,0.18),transparent_50%),radial-gradient(circle_at_50%_120%,rgba(76,37,150,0.4),transparent_55%)]" />
        <div className="absolute inset-0 cosmic-noise opacity-30" />
      </div>

      <div
        className={`fixed inset-0 -z-10 pointer-events-none hidden${isStudentRoute ? "" : " dark:block"}`}
      >
        <div className="absolute inset-0 fractal-grid opacity-20" />
      </div>

      {/* Mobile Sidebar */}
      <MobileSidebarWrapper>{sidebar}</MobileSidebarWrapper>

      {/* Main Layout Container */}
      <div className="flex h-full">
        {/* Desktop Sidebar */}
        <aside
          className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 p-4 transition-all duration-300 ease-in-out"
          style={{ width: effectivelyCollapsed ? "6.5rem" : "18rem" }}
        >
          {clonedSidebar}
        </aside>

        {/* Main Content — its own scroll container now that the outer shell
            is overflow-hidden instead of a growable min-h-screen floor.
            Content pages (Dashboard, etc.) render unbounded divs with no
            scroll mechanism of their own; they used to rely on the shell
            growing past the viewport and the document scrolling. */}
        <main
          className={`flex-1 h-full overflow-y-auto pl-0 transition-[padding-left] duration-300 ease-in-out ${effectivelyCollapsed ? "lg:pl-[6.5rem]" : "lg:pl-72"}`}
        >
          {/* Avoid a z-index stacking context here so fixed-position modals/drawers
              (e.g. CollegeDetailsDrawer) can correctly overlay the sidebar. */}
          <div className="relative">
            <Outlet
              context={{ isMiniSidebar: effectivelyCollapsed, isCollegesRoute }}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

export default PortalLayout;
